import { useId } from 'react';
import type { PlaceholderFace } from './states';
import styles from './Mascot.module.css';

/**
 * The mascot drawn when a realm has no art: a small chibi in the creator's own
 * colours, so a realm that has uploaded nothing still has a face and an animation
 * to hang on. Colours come from tokens (`--mascot-*`, `--brand-*`), which is what
 * makes it wear each realm's theme without a prop.
 *
 * Purely decorative — the wrapper carries the accessible name.
 */
export function MascotPlaceholder({ face }: { face: PlaceholderFace }) {
  const id = useId();
  const body = `${id}-body`;
  const hair = `${id}-hair`;

  return (
    <svg className={styles.art} viewBox="0 0 200 220" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={body} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--brand-400)" />
          <stop offset="1" stopColor="var(--brand-600)" />
        </linearGradient>
        <linearGradient id={hair} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="var(--mascot-hair-hi)" />
          <stop offset="1" stopColor="var(--mascot-hair)" />
        </linearGradient>
      </defs>

      {/* Back hair and ears. */}
      <path d="M40 92 L52 28 L92 58 Z" fill={`url(#${hair})`} />
      <path d="M160 92 L148 28 L108 58 Z" fill={`url(#${hair})`} />
      <ellipse cx="100" cy="110" rx="74" ry="78" fill={`url(#${hair})`} />

      {/* Shoulders. */}
      <path d="M44 214 C44 168 72 156 100 156 C128 156 156 168 156 214 Z" fill={`url(#${body})`} />
      {face === 'wave' && <path d="M150 176 C168 160 176 140 170 118" className={styles.arm} />}
      {(face === 'happy' || face === 'stars') && (
        <>
          <path d="M52 186 C34 166 30 138 38 116" className={styles.arm} />
          <path d="M148 186 C166 166 170 138 162 116" className={styles.arm} />
        </>
      )}

      {/* Face. */}
      <circle cx="100" cy="112" r="58" fill="var(--mascot-skin)" />
      <path
        d="M44 102 C52 58 148 58 156 102 C132 84 108 90 100 78 C92 90 68 84 44 102 Z"
        fill={`url(#${hair})`}
      />
      <ellipse cx="68" cy="128" rx="11" ry="7" fill="var(--mascot-blush)" />
      <ellipse cx="132" cy="128" rx="11" ry="7" fill="var(--mascot-blush)" />

      <g className={styles.eyes}>
        {face === 'happy' && (
          <>
            <path d="M62 116 Q72 104 82 116" className={styles.stroke} />
            <path d="M118 116 Q128 104 138 116" className={styles.stroke} />
          </>
        )}
        {face === 'stars' && (
          <>
            <path
              d="M72 102 l4 10 10 1 -8 7 3 10 -9 -6 -9 6 3 -10 -8 -7 10 -1 z"
              fill="var(--accent-gold)"
            />
            <path
              d="M128 102 l4 10 10 1 -8 7 3 10 -9 -6 -9 6 3 -10 -8 -7 10 -1 z"
              fill="var(--accent-gold)"
            />
          </>
        )}
        {face === 'sad' && (
          <>
            <circle cx="72" cy="116" r="7" fill="var(--mascot-ink)" />
            <circle cx="128" cy="116" r="7" fill="var(--mascot-ink)" />
            <path d="M60 102 L84 108 M140 102 L116 108" className={styles.stroke} />
            <path d="M74 126 q-3 10 0 14" className={styles.tear} />
          </>
        )}
        {(face === 'open' || face === 'wave') && (
          <>
            <circle cx="72" cy="114" r="8" fill="var(--mascot-ink)" />
            <circle cx="128" cy="114" r="8" fill="var(--mascot-ink)" />
            <circle cx="75" cy="111" r="2.6" fill="#fff" />
            <circle cx="131" cy="111" r="2.6" fill="#fff" />
          </>
        )}
      </g>

      {face === 'sad' ? (
        <path d="M90 146 Q100 138 110 146" className={styles.stroke} />
      ) : face === 'happy' || face === 'stars' ? (
        <path d="M86 140 Q100 162 114 140 Z" fill="var(--mascot-ink)" />
      ) : (
        <path d="M90 142 Q100 152 110 142" className={styles.stroke} />
      )}

      {/* A bow, for the creator's colour. */}
      <path d="M100 56 L76 40 L76 68 Z M100 56 L124 40 L124 68 Z" fill="var(--accent-rose)" />
      <circle cx="100" cy="56" r="7" fill="var(--accent-rose)" />
    </svg>
  );
}
